import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  BarElement
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './CostChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  BarElement,
);

// Type Definitions
interface RowData {

  schedule_start_time?: string;
  edited_schedule_time_start?: string;
  cost_limit?: number;
  current_qty?: number;
  mach_code?: string;
  mach_name?: string;
}

// Chartコンポーネントのプロップスの型定義
interface ChartProps {
  rowData: RowData[];
  axisField: 'schedule_start_time';
  chartTitle: string;
}

const generateChartData = (rowData: RowData[], axisField: 'schedule_start_time') => {

  const dataByField: { [key: string]: { [machCode: string]: number } } = {};
  // データを集計
  rowData.forEach(row => {
    let fieldValue = row[axisField];
    let separator = 'T'; // 区切り文字のデフォルトをTとする

    // axisFieldが'schedule_start_time'の場合に条件を確認
    if (axisField === 'schedule_start_time') {
      if (row.edited_schedule_time_start !== '' && row.edited_schedule_time_start !== null && row.edited_schedule_time_start !== undefined) {
        fieldValue = row.edited_schedule_time_start;
      } else {
        fieldValue = row.schedule_start_time;
      }
    }

    // 日付がない場合は集計しない
    if (!fieldValue) {
      return;
    }
    // schedule_start_time の場合は、日付だけを取り出す
    const dateValue = fieldValue.split(separator)[0];
    const costSum = (row.cost_limit ?? 0) * (row.current_qty ?? 0);
    const machCode = row.mach_code;

    if (dateValue && costSum && machCode) {
      if (!dataByField[dateValue]) {
        dataByField[dateValue] = {};
      }
      if (!dataByField[dateValue][machCode]) {
        dataByField[dateValue][machCode] = 0;
      }
      dataByField[dateValue][machCode] += costSum;
    }
  });


  // データを日付でソート
  const sortedKeys = Object.keys(dataByField).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  // 日付をMM/DD形式にフォーマット
  const formattedSortedKeys = sortedKeys.map(key => {
    const date = new Date(key);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  });

  // 全ての mach_code を取得
  const allMachCodes = Array.from(new Set(rowData.map(row => row.mach_code))).filter(Boolean) as string[];

  // 機械作業コードの色を定義 10で割った余りで色を決定
  const getColorByModulo = (key: string): string => {
    const parsedKey = parseInt(key, 10);
    if (isNaN(parsedKey)) {
      return `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.2)`;  // デフォルト色
    }

    const remainder = parsedKey % 4;
    switch (remainder) {
      case 0:
        return '#1773A1';
      case 1:
        return '#FF3366';
      case 2:
        return '#F9C748';
      case 3:
        return '#4AB1CC';
      default:
        return `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.2)`;  // デフォルト色
    }
  };

  // データセットの生成
  const datasets = allMachCodes.map(machCode => {
    const machName = rowData.find(row => row.mach_code === machCode)?.mach_name || 'Unknown';

    const data = sortedKeys.map(date => {
      return Number((dataByField[date]?.[machCode] || 0).toFixed(2));
    });

    const color = getColorByModulo(machCode);

    return {
      label: `機械: ${machCode}:${machName}`,
      data,
      backgroundColor: color,
      stack: 'Stack 0',
    };
  });

  return {
    labels: formattedSortedKeys,
    datasets,
  };
};


// Chartコンポーネントの実装
const CostChart: React.FC<ChartProps> = ({ rowData, axisField, chartTitle }) => {  // axisField を受け取る

  // チャートデータのstateを管理
  const [chartData, setChartData] = React.useState(generateChartData(rowData, axisField));  // axisField を渡す


  // rowDataが変更された場合にチャートを更新
  React.useEffect(() => {
    // 新しいデータでチャートを更新する関数
    const updateChartData = (newRowData: any[]) => {
      setChartData(generateChartData(newRowData, axisField));  // axisField を渡す
    };
    updateChartData(rowData); // rowDataが変更された場合にもチャートを更新
  }, [rowData, axisField]);

  const options = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: chartTitle,
        font: {
          size: 24,
        }
      },

    }, scales: {
      y: {
        ticks: {
          font: {
            size: 20,
          },
          callback: function (value: any) {
            const formattedValue = (Number(value) / 10000).toLocaleString(); // 数値を10,000で割る
            return `${formattedValue} 万円`;  // y軸のラベルに「万円」を追加
          }
        },
      },
      x: {
        ticks: {
          font: {
            size: 10,
          },
        },
      },
    },

  };
  // Barチャートをレンダリング
  return (
    <div className="single-chart-container">
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default CostChart;